import { BarSegment, STRIPE_PATTERN } from '@/components/ui';
import { Box, FormatNumber, HStack, Skeleton, Text, VStack } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

export interface BarSegmentMetricItem {
  label: string;
  value: number;
  formattedValue: string;
  color: string;
  formatAsPercent?: boolean;
  isUnavailable?: boolean;
  pattern?: boolean;
}

interface BarSegmentMetricProps {
  items: BarSegmentMetricItem[];
  isLoading?: boolean;
}

export const BarSegmentMetric: React.FC<BarSegmentMetricProps> = ({ items, isLoading }) => {
  const { t } = useTranslation();

  const hasUnavailableItem = items.some((item) => item.isUnavailable);

  return (
    <Box width="100%">
      <HStack justify="space-between" mb={2} gap={4}>
        {items.map((item) => (
          <VStack key={item.label} alignItems="flex-start" gap={1} flex={1}>
            <Skeleton height="20px" width="fit-content" loading={isLoading}>
              <HStack gap={2}>
                <Box
                  boxSize={4}
                  bg={item.color}
                  borderRadius="xs"
                  backgroundImage={item.pattern ? STRIPE_PATTERN : undefined}
                />
                <Text fontSize="15px" color="gray.fg">
                  {item.label}
                </Text>
              </HStack>
            </Skeleton>
            <Skeleton height="20px" width="fit-content" loading={isLoading}>
              {item.isUnavailable ? (
                <Text
                  fontSize="15px"
                  fontWeight="medium"
                  color="fg.muted"
                  title={t('dashboard.stats.partnerNoClicks')}
                >
                  {t('dashboard.stats.notAvailable')}
                </Text>
              ) : (
                <Text fontSize="15px" fontWeight="medium">
                  {item.formatAsPercent ? (
                    <FormatNumber
                      value={item.value / 100}
                      style="percent"
                      minimumFractionDigits={0}
                      maximumFractionDigits={2}
                    />
                  ) : (
                    item.formattedValue
                  )}
                </Text>
              )}
            </Skeleton>
          </VStack>
        ))}
      </HStack>
      {!hasUnavailableItem && (
        <Skeleton height="24px" width="100%" loading={isLoading}>
          <BarSegment
            items={items.map((item) => ({
              key: item.label,
              value: item.value,
              color: item.color,
              pattern: item.pattern,
            }))}
          />
        </Skeleton>
      )}
    </Box>
  );
};
